<template>
	<div ref="list" :class="['c-picker-list', { active: active === true }]">
		<!-- Main page -->
		<div class="c-picker-list__page">
			<template v-if="loaded">
				<Search @query="onSearch"></Search>
				<template v-if="query">
					<div class="c-picker-list__divider">
						<span> Search results </span>
					</div>
					<div
						:class="[
							'c-picker-list__item board',
							{ active: isSelected(board.id) },
						]"
						v-for="(board, i) in list"
						:key="i"
						@click="toggleSelected(board)"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M5 6H19M7 3H17M5 21H19C20.1046 21 21 20.1046 21 19V11C21 9.89543 20.1046 9 19 9H5C3.89543 9 3 9.89543 3 11V19C3 20.1046 3.89543 21 5 21Z"
								stroke="currentColor"
								stroke-linecap="round"
								stroke-width="1.5"
							/>
						</svg>
						<span>
							{{ board.name }}
						</span>
						<svg
							class="tick"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
								fill="currentColor"
							/>
						</svg>
					</div>
					<div v-if="list.length === 0" class="c-picker-list__empty">
						<span> No boards found. </span>
					</div>
				</template>
				<template v-if="recentBoards.length > 0 && !query">
					<div class="c-picker-list__divider">
						<span> Recent boards </span>
					</div>
					<div
						:class="[
							'c-picker-list__item board',
							{ active: isSelected(board.id) },
						]"
						v-for="(board, i) in recentBoards"
						:key="i"
						@click="toggleSelected(board)"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M5 6H19M7 3H17M5 21H19C20.1046 21 21 20.1046 21 19V11C21 9.89543 20.1046 9 19 9H5C3.89543 9 3 9.89543 3 11V19C3 20.1046 3.89543 21 5 21Z"
								stroke="currentColor"
								stroke-linecap="round"
								stroke-width="1.5"
							/>
						</svg>
						<span>
							{{ board.name }}
						</span>
						<svg
							class="tick"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
								fill="currentColor"
							/>
						</svg>
					</div>
				</template>
				<template v-if="!query">
					<div class="c-picker-list__divider">
						<span> Folders </span>
					</div>
					<div
						:class="[
							'c-picker-list__item folder',
							{ current: isFolderCurrent(folder) },
						]"
						v-for="folder in computedFolders"
						:key="folder.id"
						@click="setFolder(folder)"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3 5V18C3 18.5523 3.44772 19 4 19H20C20.5523 19 21 18.5523 21 18V8C21 7.44772 20.5523 7 20 7H12.5352C12.2008 7 11.8886 6.8329 11.7031 6.5547L10.2969 4.4453C10.1114 4.1671 9.79917 4 9.46482 4H4C3.44772 4 3 4.44772 3 5Z"
								stroke="currentColor"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						<span>
							{{ folder.name }}
						</span>
						<span :class="[{ muted: folder.boards.length === 0 }]">
							{{ folder.boards.length }}
						</span>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M10 16L13.4697 12.5303C13.7626 12.2374 13.7626 11.7626 13.4697 11.4697L10 8"
								stroke="currentColor"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</div>
					<div class="c-picker-list__empty" v-if="computedFolders.length === 0">
						<span> No folders created </span>
					</div>
				</template>
			</template>

			<template v-else>
				<div class="c-picker-list__loader">
					<span class="c-spinner"></span>
				</div>
			</template>
		</div>

		<!-- Overlay -->
		<div class="c-picker-list__overlay" v-if="currentFolder">
			<div class="c-picker-list__header" @click="onHeaderClicked">
				<article>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M15 19L8 12L15 5"
							stroke="currentColor"
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
						/>
					</svg>

					<span>
						{{ currentFolder.name }}
					</span>

					<svg
						class="create-board"
						@click="createBoard = true"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16 12.9999C16.5523 12.9999 17 12.5522 17 11.9999C17 11.4476 16.5523 10.9999 16 10.9999L13 11V8.00012C13 7.44784 12.5523 7.00012 12 7.00012C11.4477 7.00012 11 7.44784 11 8.00012V11L7.99997 11.0001C7.44769 11.0001 6.99998 11.4479 7 12.0001C7.00002 12.5524 7.44774 13.0001 8.00003 13.0001L11 13V16C11 16.5523 11.4477 17 12 17C12.5523 17 13 16.5523 13 16V13L16 12.9999Z"
							fill="currentColor"
						/>
					</svg>
				</article>
				<CreateBoard
					:folder="currentFolder"
					@closeCreateBoard="onCloseCreateBoard"
					@createBoard="onBoardCreated"
					:baseUrl="baseurl"
					:active="createBoard"
				></CreateBoard>
			</div>
			<div
				:class="['c-picker-list__item board', { active: isSelected(board.id) }]"
				v-for="board in currentFolder.boards"
				:key="board.id"
				@click="toggleSelected(board)"
			>
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M5 6H19M7 3H17M5 21H19C20.1046 21 21 20.1046 21 19V11C21 9.89543 20.1046 9 19 9H5C3.89543 9 3 9.89543 3 11V19C3 20.1046 3.89543 21 5 21Z"
						stroke="currentColor"
						stroke-linecap="round"
						stroke-width="1.5"
					/>
				</svg>
				<span>
					{{ board.name }}
				</span>
				<svg
					class="tick"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
						fill="currentColor"
					/>
				</svg>
			</div>
			<div v-if="currentFolderBoards.length === 0" class="c-picker-list__empty">
				<span> No boards in here </span>
			</div>
		</div>
	</div>
</template>

<script>
import CreateBoard from './create-board';
import InputText from '@swipekit/components/form/input-text';
import Fuse from 'fuse.js';
import { format } from 'date-fns';
import Search from './search';

export default {
	components: {
		CreateBoard,
		InputText,
		Search,
	},

	data: function () {
		return {
			query: '',

			currentFolder: null,

			createBoard: false,
		};
	},

	props: {
		selected: {},
		baseurl: {},
		active: {
			type: Boolean,
			default: false,
		},
		folders: {},
		boards: {},
		recentBoardIds: {
			default: function () {
				return [];
			},
		},

		loaded: {
			type: Boolean,
			default: false,
		},

		mode: {
			default: 'extension',
		},
	},

	watch: {
		// createBoard: function () {
		// 	if (this.createBoard) {
		// 		this.$nextTick(() => {
		// 			let el = this.$el.querySelector('form input[type="text"]');
		// 			if (el) {
		// 				el.focus();
		// 			}
		// 		});
		// 	}
		// },
		// active: function () {
		// 	if (!this.active) {
		// 		return;
		// 	}
		// 	if (this.loaded) {
		// 		return;
		// 	}
		// 	if (this.active) {
		// 		let selector = `.c-picker-list__input input`;
		// 		let el = this.$el.querySelector(selector);
		// 		if (el) {
		// 			el.focus();
		// 		}
		// 	}
		// },

		computedFolders: function () {
			if (this.computedFolders.length === 1 && !this.currentFolder) {
				this.setFolder(this.computedFolders[0]);
			}
		},
	},

	computed: {
		recentBoards: function () {
			if (this.recentBoardIds.length === 0) {
				return [];
			}
			const recentBoardIds = this.recentBoardIds;
			let boards = this.boards;
			let newBoards = [];
			for (let i = 0; i < boards.length; i++) {
				const board = boards[i];
				if (recentBoardIds.includes(board.id)) {
					newBoards.push(board);
				}
			}
			return newBoards;
		},
		computedFolders: function () {
			let currentFolders = this.folders || [];
			let folders = JSON.parse(JSON.stringify(currentFolders));
			let boards = this.boards;

			for (let i = 0; i < folders.length; i++) {
				let folder = folders[i];

				folder.boards = [];

				for (let j = 0; j < boards.length; j++) {
					const board = boards[j];

					if (board.folderId === folder.id) {
						folder.boards.push(board);
					}
				}

				folders[i] = folder;
			}

			return folders;
		},
		currentFolderBoards: function () {
			if (!this.currentFolder) {
				return [];
			}
			let boards = this.boards;
			let newBoards = [];
			for (let i = 0; i < boards.length; i++) {
				let board = boards[i];
				if (board.folderId === this.currentFolder.id) {
					newBoards.push(board);
				}
			}
			return newBoards;
		},
		list: function () {
			let selected = this.selected;

			let items = this.boards || [];

			items = JSON.parse(JSON.stringify(items));

			if (this.query) {
				let options = {
					keys: ['name'],
				};

				let fuse = new Fuse(items, options);

				items = fuse.search(this.query);

				items = items.map((item) => {
					return item.item;
				});
			} else {
				items = items.sort((a, b) => {
					return a.order - b.order;
				});
			}

			items = items.map((item) => {
				let i = selected.indexOf(item.id);

				item.active = false;

				item.createdAt = format(new Date(item.createdAt), `do MMM yyyy`);

				if (i === -1) {
				} else {
					item.active = true;
				}

				return item;
			});

			items = items.sort((a, b) => {
				return a.order - b.order;
			});

			return items;
		},
	},

	methods: {
		isFolderCurrent: function (folder) {
			let selected = this.selected;
			if (selected.length === 0) {
				return;
			}

			let boards = folder.boards;
			if (boards.length === 0) {
				return;
			}

			let condition = false;

			for (let i = 0; i < boards.length; i++) {
				let board = boards[i];
				if (selected.includes(board.id)) {
					condition = true;
					break;
				}
			}

			return condition;
		},
		onSearch: function (e) {
			this.query = e;
		},
		onHeaderClicked: function (e) {
			let target = e.target;

			let isCreateBoardButton = target.closest('.create-board');
			if (isCreateBoardButton) {
				return;
			}

			let isCreateBoard = target.closest('.c-picker-create-board');
			if (isCreateBoard) {
				return;
			}

			// did this so it doesn't interfere with clickoutside
			setTimeout(() => {
				this.currentFolder = null;
			}, 2);
		},
		onBoardCreated: function () {
			this.createBoard = false;
			this.$emit('createBoard');
		},
		onCloseCreateBoard: function () {
			this.createBoard = false;
		},
		isSelected: function (id) {
			let selected = this.selected;

			let i = this.selected.indexOf(id);

			if (i !== -1) {
				return true;
			}

			return false;
		},
		setFolder: function (folder) {
			this.currentFolder = folder;
		},
		refresh: function () {
			//this.loadBoards();
		},
		onCreateBoard: function () {
			this.$store.dispatch('boards/setAdd', 1);
		},
		toggleSelected: function (item) {
			let i = this.selected.indexOf(item.id);
			let selected = this.selected;
			if (i === -1) {
				selected.push(item.id);
			} else {
				let i = selected.indexOf(item.id);
				selected.splice(i, 1);
			}

			this.$emit('change', selected);
		},
		handleClickOutside(event) {
			if (!this.active) {
				return;
			}

			let condition = event.target.closest('.swipekit-button-new > button');
			if (condition) {
				return;
			}

			let isInsideList = event.target.closest('.c-picker-list');
			if (isInsideList) {
				return;
			}

			let isSwipekitPicker = event.target.closest('swipekit-picker');
			if (isSwipekitPicker) {
				return;
			}

			this.currentFolder = null;
			this.$emit('clickOutside');
		},
	},

	mounted() {
		document.body.addEventListener('click', this.handleClickOutside);
	},
	beforeDestroy() {
		document.body.removeEventListener('click', this.handleClickOutside);
	},
};
</script>

<style lang="scss">
.c-picker-list {
	width: 100%;
	position: absolute;
	z-index: 101;
	top: 100%;
	left: 0;
	height: 329.12px;
	overflow-y: auto;
	border-radius: 10px;
	border: var(--color-bg-5) solid 1px;
	background-color: #fff;
	box-shadow: rgba(23, 23, 23, 0.1) 0 2px 12px;
	transform: translateY(-20px);
	opacity: 0;
	transition: all 180ms cubic-bezier(0.215, 0.61, 0.355, 1);
	pointer-events: none;

	&::-webkit-scrollbar {
		width: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background: hsl(var(--p), 4%, 65%);
		border-radius: 0;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: hsl(var(--p), 4%, 65%);
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&.active {
		transform: translateY(0);
		opacity: 1;
		pointer-events: initial;
	}

	&__loader {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;

		.c-spinner {
			border-bottom-color: #665bdc;
		}
	}

	&__divider {
		min-height: 25px;
		position: relative;
		padding: 4px 8px;
		font-size: 12px;
		opacity: 0.65;
		display: flex;
		align-items: center;

		span {
			position: relative;
			z-index: 1;
			display: inline-block;
			background-color: #fff;
			padding-right: 8px;
			text-transform: uppercase;
			font-weight: 600;
		}

		&:after {
			content: '';
			position: absolute;
			top: 11px;
			right: 4px;
			width: calc(100% - 30px);
			height: 1px;
			background-color: #000;
			opacity: 0.3;
			border: none;
		}
	}

	&__header {
		position: relative;
		padding: 4px 8px;
		padding-left: 4px;
		font-size: 14px;

		cursor: pointer;

		article {
			display: flex;
			align-items: center;

			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;

			span {
				opacity: 0.8;
			}

			svg {
				opacity: 0.8;
				&:last-of-type {
					opacity: 1;
					margin-left: auto;
					color: var(--color-primary);
				}
			}
		}

		section {
			position: relative;
		}
	}

	&__item {
		display: grid;
		grid-template-columns: 1fr;
		cursor: pointer;
		padding: 5px;
		background-color: #fff;
		transition: all 60ms linear;

		span {
			font-size: 14px;
		}

		svg {
			width: 20px;
			height: 20px;
		}

		&.folder {
			grid-template-columns: 24px 1fr min-content 20px;

			span {
				&:last-of-type {
					line-height: 1.45;
					font-size: 13px;
					font-feature-settings: 'case' 0, 'dlig' 0, 'frac' 0, 'dnom' 0,
						'numr' 0, 'subs' 0, 'sups' 0, 'tnum', 'zero' 0, 'ss01', 'ss02' 0,
						'ss03' 0, 'ss04', 'ss05' 0, 'ss06' 0, 'ss07' 0, 'ss08' 0, 'cv01' 0,
						'cv02' 0, 'cv03' 0, 'cv04' 0, 'cv05' 0, 'cv06' 0, 'cv07' 0, 'cv08' 0,
						'cv09' 0, 'cv10' 0, 'cv11' 0, 'cv12' 0, 'cv13' 0, 'cpsp' 0, 'c2sc' 0,
						'salt' 0, 'aalt' 0, 'calt', 'ccmp', 'locl', 'kern';
				}

				&.muted {
					opacity: 0.4;
				}
			}

			&.current {
				background-color: hsl(240, 50%, 95%);
			}
		}

		&.board {
			grid-template-columns: 24px 1fr 24px;

			.tick {
				opacity: 0;
				margin-left: 4px;
			}
		}

		&.active {
			&.board {
				.tick {
					opacity: 1;
				}
			}
		}

		&:hover,
		&:active {
			background-color: #665bdc !important;
			color: #fff;
		}
	}

	&__empty {
		padding: 8px;
		opacity: 0.75;
		text-align: center;
		font-size: 12px;
	}

	&__input {
		padding: var(--margin);

		.c-input-text {
			padding-bottom: 0;
		}

		input {
			appearance: none;
			display: block;
			border: none;
			width: 100%;
			height: 100%;
			padding: 4px 8px 4px 8px;
			padding-right: 32px;
			background-color: var(--color-bg-3);
			border-radius: 6px;
		}
	}

	&__default {
		font-size: 13px;
		background-color: hsl(80, 95%, 95%);
		height: 100%;
		display: flex;
		align-items: center;
		text-align: left;
		border-radius: 8px;
	}

	&__overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: #fff;
	}
}
</style>
